import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { MenuItem, MenuItemChild, useMenu } from 'hooks/utils/useMenu';

import { useAuthStore } from 'store/index';

import type { Module } from 'types/auth/Module';

interface PermissionGuardProps {
  children: ReactNode;
  /** Module key for permission check */
  moduleKey: Module['name'];
}
export function PermissionGuard({ children, moduleKey }: PermissionGuardProps) {
  const { userAccessibleModules, modulesLoaded } = useAuthStore();
  const { links, hasModulePermission } = useMenu(userAccessibleModules);
  const location = useLocation();

  // If modules haven't loaded yet, render nothing (fail closed)
  // The API loading overlay will show while data is being fetched
  // If the API fails, modulesLoaded will be set to true with empty modules (denying access)
  if (!modulesLoaded) {
    return null;
  }

  // Check permission based on the provided prop
  const hasAccess = links.some((link) => {
    if (hasModulePermission(link.permissionKey, moduleKey)) {
      return true;
    }
    // Also check children for component-based checks
    if (link.children) {
      return link.children.some((child) =>
        hasModulePermission(child.permissionKey, moduleKey)
      );
    }
    return false;
  });

  if (hasAccess) {
    return <>{children}</>;
  }

  // Find first available path from the pre-filtered links
  // (links already filtered by useMenu which respects permissions and demo)
  const findFirstPath = (
    items: MenuItem[] | MenuItemChild[]
  ): string | null => {
    for (const item of items) {
      if (item.path) return item.path;
      if ('children' in item && item.children) {
        const childPath = findFirstPath(item.children);
        if (childPath) return childPath;
      }
    }
    return null;
  };

  const firstPath = findFirstPath(links);

  if (firstPath) {
    return <Navigate to={`/${firstPath}`} replace state={{ from: location }} />;
  }

  return <Navigate to="/404" replace state={{ from: location }} />;
}
